const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });
  const text = await res.text();
  const json = parseJson(text);
  if (!res.ok) {
    const err =
      typeof json === 'object' && json !== null && 'error' in json
        ? String((json as { error?: unknown }).error)
        : text.trim() || `http_${res.status}`;
    throw new Error(err);
  }
  if (text && json === null) {
    throw new Error('invalid_json_response');
  }
  return json as T;
}

function parseJson(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export type CheckoutResponse = { sessionId: string; url: string };

export type TrialClassChoice =
  | 'BOXING'
  | 'MUAY_THAI'
  | 'MMA'
  | 'BJJ'
  | 'KIDS_BOXING'
  | 'KIDS_BJJ'
  | 'KIDS_WRESTLING';
export type TrialDayChoice =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY';

export async function startTrialCheckout(input: {
  name: string;
  email: string;
  phone: string;
  classChoice: TrialClassChoice;
  dayChoice: TrialDayChoice;
  /** Specific calendar date the member will attend, `YYYY-MM-DD`. */
  classDate: string;
  /** Specific schedule slot id (e.g. "mon-620-boxing-fund"). */
  slotId?: string;
  slotTitle?: string;
  slotStartTime?: string;
  slotEndTime?: string;
  isMinor: boolean;
  parentEmail?: string;
  /** Required — server rejects unauthenticated checkout (IDOR guard on the User upsert). */
  authToken: string;
}): Promise<CheckoutResponse> {
  return http<CheckoutResponse>('/api/trial/checkout', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface TrialConfirmation {
  name: string;
  classChoice: TrialClassChoice;
  dayChoice: TrialDayChoice;
  classDate: string | null;
  slotTitle: string | null;
  slotStartTime: string | null;
  slotEndTime: string | null;
  amountCents: number;
  status: string;
  isMinor: boolean;
}

export async function getTrialConfirmation(sessionId: string): Promise<TrialConfirmation | null> {
  try {
    return await http<TrialConfirmation>(
      `/api/trial/confirmation?session_id=${encodeURIComponent(sessionId)}`
    );
  } catch (err) {
    if (String((err as Error).message ?? err) === 'trial_not_found') return null;
    throw err;
  }
}

export type MembershipTier = 'ONE_PROGRAM' | 'TWO_PROGRAMS' | 'UNLIMITED' | 'KIDS';
export type MembershipInterval = 'MONTHLY' | 'YEARLY';
export type Program = 'Boxing' | 'MuayThai' | 'MMA' | 'BJJ' | 'Kids';

export async function startMembershipCheckout(input: {
  name: string;
  email: string;
  phone: string;
  tier: MembershipTier;
  interval: MembershipInterval;
  athletes: boolean;
  /** Server-allowlisted discount code (from `?code=...` on the QR URL). */
  discountCode?: string;
  programs: Program[];
  /** Required — server rejects unauthenticated checkout (IDOR guard on the User upsert). */
  authToken: string;
}): Promise<CheckoutResponse> {
  return http<CheckoutResponse>('/api/membership/checkout', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface PriceQuote {
  amountCents: number;
  lookupKey: string;
}

export interface MembershipPricing {
  monthly: PriceQuote;
  yearly: PriceQuote | null;
}

export async function getMembershipPricing(input: {
  tier: MembershipTier;
  athletes: boolean;
}): Promise<MembershipPricing> {
  const q = new URLSearchParams({
    tier: input.tier,
    athletes: input.athletes ? 'true' : 'false',
  });
  return http<MembershipPricing>(`/api/membership/pricing?${q.toString()}`);
}

export interface MembershipSummary {
  programs: string[];
  tier: MembershipTier;
  interval: MembershipInterval;
  athletes: boolean;
  monthlyAmountCents: number;
  commitmentMonths: number | null;
  /** Lets the success page deep-link to /account. Null if Stripe has not attached it yet. */
  subscriptionId: string | null;
}

/** Plan summary for the success page, keyed by Stripe Checkout Session id. */
export async function getMembershipSummary(sessionId: string): Promise<MembershipSummary | null> {
  try {
    return await http<MembershipSummary>(
      `/api/membership/summary?session_id=${encodeURIComponent(sessionId)}`
    );
  } catch {
    return null;
  }
}

export async function startCustomerPortal(input: {
  authToken: string;
  subscriptionId: string;
}): Promise<{ url: string }> {
  return http('/api/membership/portal', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export type WaiverKind =
  | 'LIABILITY_ADULT'
  | 'LIABILITY_MINOR'
  | 'SPARRING_ADULT'
  | 'SPARRING_MINOR'
  | 'MEMBERSHIP_TERMS'
  | 'BILLING_TERMS';

export type Waiver = { kind: WaiverKind; version: number; title: string; text: string };

export async function listWaivers(kinds: WaiverKind[]): Promise<{ waivers: Waiver[] }> {
  const q = kinds.length ? `?kinds=${kinds.join(',')}` : '';
  return http<{ waivers: Waiver[] }>(`/api/waiver${q}`);
}

export type SignerRelationship = 'PARENT' | 'GUARDIAN';

export async function signWaiver(input: {
  authToken: string;
  kind: WaiverKind;
  version: number;
  typedSignature: string;
  intent: string;
  esignConsent: true;
  esignDisclosure: string;
  participantName?: string;
  signerRelationship?: SignerRelationship;
  personalInfo?: {
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
    dateOfBirth?: string;
    medicalConditions?: string;
  };
}): Promise<{ signatureId: string; contentHash: string; signedAt: string }> {
  return http('/api/waiver/sign', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function submitWaivers(input: {
  authToken: string;
  requiredKinds: WaiverKind[];
}): Promise<{ sent: true; waiverCount: number }> {
  return http('/api/waiver/submit', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function createIdentitySession(authToken: string): Promise<{
  sessionId: string;
  clientSecret: string;
}> {
  return http('/api/identity/session', {
    method: 'POST',
    body: JSON.stringify({ authToken }),
  });
}

export async function getIdentityStatus(authToken: string): Promise<{
  status: 'unverified' | 'requires_input' | 'processing' | 'verified' | 'canceled';
  verifiedAt: string | null;
}> {
  return http('/api/identity/status', {
    headers: { Authorization: `Bearer ${authToken}` },
  });
}

export async function checkAccount(email: string): Promise<{
  exists: boolean;
  hasPassword: boolean;
}> {
  return http('/api/auth/check', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export interface PasswordSession {
  authToken: string;
  refreshToken: string;
  expiresAt: number;
  userId: string;
  email: string;
}

export async function passwordSignIn(input: {
  email: string;
  password: string;
}): Promise<PasswordSession> {
  return http('/api/auth/password', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function passwordSignUp(input: {
  email: string;
  password: string;
}): Promise<PasswordSession> {
  return http('/api/auth/password/signup', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function adminLogin(password: string): Promise<{ ok: true }> {
  return http('/api/admin/login', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export async function adminFetchIdDocument(userId: string): Promise<{
  status: string | null;
  verifiedAt: string | null;
  links: Array<{ label: string; url: string; expiresAt: string }>;
}> {
  return http(`/api/admin/users/${encodeURIComponent(userId)}/id-document`);
}

type ContentProgram = { slug: string; title: string; blurb: string; bullets: string[] };
type Instructor = { slug: string; name: string; title: string; bio: string };

export type ScheduleDay = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY';

export type ScheduleSlot = {
  id: string;
  day: ScheduleDay;
  startTime: string;
  endTime: string;
  title: string;
  category: 'boxing' | 'muay-thai' | 'bjj' | 'mma' | 'kids' | 'wrestling' | 'sparring' | 'mixed';
  level?: 'beginners' | 'intermediate' | 'advanced' | 'fighters' | 'mixed' | 'kids';
  trialEligible: boolean;
  notes?: string;
};

export type TrainingPlan = {
  discipline: string;
  levels: ReadonlyArray<{
    title: string;
    notes?: string;
    workouts?: ReadonlyArray<{
      day: ScheduleDay;
      startTime: string;
      endTime: string;
    }>;
    /** Temporary fallback for the pre-workout-window API response. */
    slotIds?: readonly string[];
  }>;
};

export type EquipmentRequirements = {
  beginners: string;
  boxingIntermediate: string;
  muayThaiIntermediate: string;
  schoolTShirt: string;
};

export async function getPrograms(): Promise<{ programs: ContentProgram[] }> {
  return http('/api/content/programs');
}

export async function getInstructors(): Promise<{ instructors: Instructor[] }> {
  return http('/api/content/instructors');
}

export async function getSchedule(): Promise<{
  schedule: ScheduleSlot[];
  trainingPlans: TrainingPlan[];
  equipment: EquipmentRequirements;
}> {
  return http('/api/content/schedule');
}

export async function getTrialSlots(): Promise<{ slots: ScheduleSlot[] }> {
  return http('/api/content/trial-slots');
}

export interface SignatureView {
  id: string;
  signedAt: string;
  typedSignature: string;
  intent: string;
  ip: string;
  userAgent: string;
  contentHash: string;
  contentSnapshot: string;
  waiver: { kind: WaiverKind; version: number; title: string };
}

export async function getSignatureView(id: string, token: string): Promise<SignatureView> {
  return http(`/api/waiver/signature/${encodeURIComponent(id)}?t=${encodeURIComponent(token)}`);
}

export interface MembershipCodeResult {
  valid: true;
  athletes: boolean;
}

/**
 * Validate a /join?code=... URL. Returns `null` on 404 (invalid code) so the
 * caller can render a "not found" page. Throws on transport errors.
 */
export async function validateMembershipCode(code: string): Promise<MembershipCodeResult | null> {
  const r = await fetch(`${BASE}/api/membership/validate-code?code=${encodeURIComponent(code)}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`validate-code ${r.status}`);
  return (await r.json()) as MembershipCodeResult;
}

export async function submitContact(input: {
  name: string;
  email: string;
  phone?: string;
  message: string;
}): Promise<{ ok: true }> {
  return http('/api/content/contact', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
